// The wire contract for POST <builder>/api/capture, shared by the gate and the Worker.
// The Worker answers with codes; the gate turns each code into the deck's copy.

import type { EmailType } from "../leadgen/work-email";
import type { BusinessType, Category, Platform } from "./templates";
import { GATE_COPY } from "./copy";

export type CaptureField = "email" | "role" | "businessType" | "accounts";
export type CaptureFieldError = "required" | "invalid" | "disposable" | "undeliverable";
export type CaptureErrorCode =
  | "invalid"
  | "turnstile_failed"
  | "rate_limited"
  | "email_rate_limited"
  | "server_error";

export interface CaptureInputs {
  goal: Category;
  platform: Platform;
  brand: string;
  audience: string;
  tone: string;
  pillars: string;
  promptId: string;
  /** Only the template slot keys; the Worker rejects any other key. */
  slots: Partial<Record<string, string>>;
}

export interface CaptureRequestBody {
  email: string;
  role: string;
  businessType: BusinessType | "";
  accounts: string;
  newsletter: boolean;
  inputs: CaptureInputs;
  /** Read by readTurnstileToken in src/server/security/turnstile.ts. */
  turnstile_token?: string;
}

/** Double opt-in: the link is emailed, never returned. */
export interface CaptureSuccess {
  status: "pending";
  emailType: EmailType;
}

export interface CaptureFailure {
  error: CaptureErrorCode;
  fields?: Partial<Record<CaptureField, CaptureFieldError>>;
}

export const CAPTURE_FIELD_ORDER: readonly CaptureField[] = [
  "email",
  "role",
  "businessType",
  "accounts",
];

export function fieldErrorCopy(field: CaptureField, error: CaptureFieldError): string {
  if (field === "email") {
    if (error === "required") return GATE_COPY.emailRequired;
    if (error === "disposable") return GATE_COPY.emailDisposable;
    if (error === "undeliverable") return GATE_COPY.emailUndeliverable;
    return GATE_COPY.emailInvalid;
  }
  if (field === "role") return GATE_COPY.roleRequired;
  if (field === "businessType") return GATE_COPY.businessTypeRequired;
  return GATE_COPY.accountsRequired;
}

export function formErrorCopy(error: CaptureErrorCode): string {
  if (error === "turnstile_failed") return GATE_COPY.turnstileFailed;
  if (error === "rate_limited") return GATE_COPY.rateLimited;
  if (error === "email_rate_limited") return GATE_COPY.emailRateLimited;
  return GATE_COPY.serverError;
}
